/**
 * 送料無料キャンペーンの告知（上部バー／POPUP）
 *
 * - 上部バー: 残り日数を端末の現在時刻で更新し、終了日時を過ぎていれば取り除いて元の上部バーに戻す
 *   （ページキャッシュで終了前のHTMLが返った場合の保険。通常は Liquid 側で出力自体を止める）
 * - POPUP: 一定時間の経過、または少しスクロールした時点で表示する。
 *   ×ボタン／背景クリック／Esc／リンク押下のいずれかで閉じた記録を localStorage に残し、同じキャンペーン期間中は再表示しない
 *
 * 設定値（終了日時・ストレージキーなど）は snippets/campaign-shipping.liquid から data 属性で受け取る。
 */
(function () {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const BAR_SELECTOR = '[data-campaign-bar]';
  const REPLACED_BAR_SELECTOR = '[data-campaign-replaced]';
  const DAYS_SELECTOR = '[data-campaign-days]';
  const CLOSE_BUTTON_SELECTOR = '[data-campaign-popup-close]';
  const CTA_SELECTOR = '[data-campaign-popup-cta]';
  const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const BAR_EMPHASIZED_CLASS = 'campaign-bar--emphasized';
  const SCROLL_LOCK_CLASS = 'campaign-popup-lock';
  const FALLBACK_DELAY_MS = 3000;
  const FALLBACK_SCROLL_THRESHOLD = 200;
  /** 他のモーダルと重ならないよう、開いている間は少し待ってから再判定する */
  const BLOCKED_RETRY_DELAY_MS = 1500;
  const BLOCKING_DIALOG_SELECTOR = [
    '#AgeVerificationOverlay:not([hidden])',
    '[data-wine-minimum-overlay]:not([hidden])',
    'cart-drawer.active',
    '#cart-notification.active',
    'details.menu-opening',
    'modal-dialog[open]',
    'dialog[open]:not(.campaign-popup)',
  ].join(',');

  /**
   * ISO 8601 形式の日時をミリ秒に変換する
   * @param {string | undefined} value
   * @returns {number} 解釈できない場合は NaN
   */
  function parseEndTime(value) {
    return value ? Date.parse(value) : Number.NaN;
  }

  /**
   * @param {number} endTime
   * @returns {boolean} 終了日時が有効で、まだ過ぎていないか
   */
  function isCampaignRunning(endTime) {
    return Number.isFinite(endTime) && Date.now() < endTime;
  }

  /**
   * 終了までの残り日数（当日中なら 1 日）
   * @param {number} endTime
   * @returns {number}
   */
  function getRemainingDays(endTime) {
    return Math.max(0, Math.ceil((endTime - Date.now()) / DAY_MS));
  }

  /**
   * data 属性の値を非負整数として取得する
   * @param {string | undefined} value
   * @param {number} fallback 数値として解釈できない場合に採用する値
   * @returns {number}
   */
  function parseNonNegativeInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }

  /**
   * @param {string | undefined} storageKey
   * @param {string | undefined} campaignId
   * @returns {boolean} このキャンペーンの POPUP を閉じた記録があるか
   */
  function isPopupDismissed(storageKey, campaignId) {
    if (!storageKey || !campaignId) return false;

    try {
      return window.localStorage.getItem(storageKey) === campaignId;
    } catch (error) {
      // プライベートブラウズなどでストレージが使えない場合は、未読として扱う（告知の機会を優先する）
      return false;
    }
  }

  /**
   * @param {string | undefined} storageKey
   * @param {string | undefined} campaignId
   */
  function saveDismissedPopup(storageKey, campaignId) {
    if (!storageKey || !campaignId) return;

    try {
      window.localStorage.setItem(storageKey, campaignId);
    } catch (error) {
      console.warn('[campaign-shipping] POPUPを閉じた状態を保存できませんでした', error);
    }
  }

  /**
   * @param {ParentNode} root
   * @param {number} endTime
   */
  function renderRemainingDays(root, endTime) {
    const remainingDays = String(getRemainingDays(endTime));
    root.querySelectorAll(DAYS_SELECTOR).forEach((element) => {
      element.textContent = remainingDays;
    });
  }

  /**
   * キャンペーンバーを取り除き、差し替えていた元の上部バーを表示し直す
   * @param {HTMLElement} bar
   */
  function restoreReplacedBar(bar) {
    bar.parentElement?.querySelectorAll(REPLACED_BAR_SELECTOR).forEach((element) => {
      element.hidden = false;
    });
    bar.remove();
  }

  function syncCampaignBars() {
    document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
      const endTime = parseEndTime(bar.dataset.endAt);

      if (!isCampaignRunning(endTime)) {
        restoreReplacedBar(bar);
        return;
      }

      renderRemainingDays(bar, endTime);
      if (isPopupDismissed(bar.dataset.storageKey, bar.dataset.campaignId)) {
        bar.classList.add(BAR_EMPHASIZED_CLASS);
      }
    });
  }

  /** POPUP を閉じた後も告知を続けるため、上部バーを強調色に切り替える */
  function emphasizeCampaignBars() {
    document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
      bar.classList.add(BAR_EMPHASIZED_CLASS);
    });
  }

  /** @returns {boolean} 年齢確認・カートドロワーなど、他のモーダルが開いているか */
  function isOtherDialogOpen() {
    return document.querySelector(BLOCKING_DIALOG_SELECTOR) !== null;
  }

  class CampaignPopup extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector('dialog');
      this.endTime = parseEndTime(this.dataset.endAt);
      this.campaignId = this.dataset.campaignId;
      this.storageKey = this.dataset.storageKey;
      this.delayMs = parseNonNegativeInteger(this.dataset.delay, FALLBACK_DELAY_MS);
      this.scrollThreshold = parseNonNegativeInteger(this.dataset.scrollThreshold, FALLBACK_SCROLL_THRESHOLD);
      // テーマエディタでは確認のたびに表示できるよう、閉じた記録を読み書きしない
      this.isDesignMode = window.Shopify?.designMode === true;

      this.openTimerId = undefined;
      this.previouslyFocusedElement = null;

      this.requestOpen = this.requestOpen.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onCtaClick = this.onCtaClick.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      if (!(this.dialog instanceof HTMLDialogElement) || typeof this.dialog.showModal !== 'function') return;
      if (!isCampaignRunning(this.endTime) || this.isDismissed()) return;

      renderRemainingDays(this, this.endTime);

      this.dialog.addEventListener('click', this.onDialogClick);
      // ×ボタン・背景クリック・Esc のどの閉じ方でも close イベントを通るため、ここで後処理をまとめる
      this.dialog.addEventListener('close', this.onDialogClose);
      this.querySelectorAll(CLOSE_BUTTON_SELECTOR).forEach((button) => button.addEventListener('click', this.close));
      this.querySelectorAll(CTA_SELECTOR).forEach((link) => link.addEventListener('click', this.onCtaClick));

      // 画像は lazy のため非表示中は読み込まれない。表示待ちの間に取得しておき、開いた瞬間の空白を防ぐ
      this.dialog.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });

      this.openTimerId = window.setTimeout(this.requestOpen, this.delayMs);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.clearOpenTriggers();
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    }

    /** @returns {boolean} */
    isDismissed() {
      return !this.isDesignMode && isPopupDismissed(this.storageKey, this.campaignId);
    }

    markAsDismissed() {
      if (!this.isDesignMode) saveDismissedPopup(this.storageKey, this.campaignId);
    }

    clearOpenTriggers() {
      window.clearTimeout(this.openTimerId);
      this.openTimerId = undefined;
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (window.scrollY >= this.scrollThreshold) this.requestOpen();
    }

    /** 時間経過・スクロールのどちらか早い方で一度だけ開く */
    requestOpen() {
      this.clearOpenTriggers();

      if (!this.isConnected || this.dialog.open) return;
      if (!isCampaignRunning(this.endTime) || this.isDismissed()) return;

      if (isOtherDialogOpen()) {
        this.openTimerId = window.setTimeout(this.requestOpen, BLOCKED_RETRY_DELAY_MS);
        return;
      }

      this.open();
    }

    open() {
      this.previouslyFocusedElement = document.activeElement;
      renderRemainingDays(this, this.endTime);
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
      document.addEventListener('keydown', this.onDocumentKeydown);
      // showModal により POPUP 外は操作不可になり、先頭のフォーカス可能要素（×ボタン）へフォーカスが移る
      this.dialog.showModal();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onDialogClose() {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.markAsDismissed();
      emphasizeCampaignBars();

      if (this.previouslyFocusedElement instanceof HTMLElement && this.previouslyFocusedElement.isConnected) {
        this.previouslyFocusedElement.focus({ preventScroll: true });
      }
      this.previouslyFocusedElement = null;
    }

    /**
     * 背景（::backdrop）のクリックは dialog 要素自身へのクリックとして届くため、座標が POPUP の外なら閉じる
     * @param {MouseEvent} event
     */
    onDialogClick(event) {
      if (event.target !== this.dialog) return;

      const rect = this.dialog.getBoundingClientRect();
      const isInsideDialog =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (!isInsideDialog) this.close();
    }

    /** リンク先へ移動した後に再表示しないよう、遷移前に閉じた扱いにする */
    onCtaClick() {
      this.markAsDismissed();
    }

    /**
     * Esc で閉じる（dialog 標準の Esc 処理はブラウザごとに挙動差があるため、明示的に閉じる）。
     * Tab／Shift+Tab ではフォーカスが POPUP の外（ブラウザのUIを含む）へ出ないよう、先頭と末尾で循環させる
     * @param {KeyboardEvent} event
     */
    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const isFocusInside = this.dialog.contains(activeElement);

      if (event.shiftKey && (activeElement === firstElement || !isFocusInside)) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (activeElement === lastElement || !isFocusInside)) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  syncCampaignBars();

  if (!customElements.get('campaign-popup')) {
    customElements.define('campaign-popup', CampaignPopup);
  }
})();
